import React, { useEffect } from 'react';
import GlobalHeader from '../components/GlobalHeader';
import GlobalFooter from '../components/GlobalFooter';
import LegalHero from '../components/LegalHero';
import PageSEO from '../components/PageSEO';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({
        top: el.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };

  const navItems = [
    { id: 'sec-1', label: '1. Who We Are' },
    { id: 'sec-2', label: '2. Scope of This Policy' },
    { id: 'sec-3', label: '3. Information We Collect About You' },
    { id: 'sec-4', label: '4. How We Use Information' },
    { id: 'sec-5', label: '5. Legal Bases for Processing' },
    { id: 'sec-6', label: '6. Analytics We Provide About Your Website Visitors' },
    { id: 'sec-7', label: '7. Sharing Information' },
    { id: 'sec-8', label: '8. International Data Transfers' },
    { id: 'sec-9', label: '9. How Long We Keep Information' },
    { id: 'sec-10', label: '10. Your Privacy Rights' },
    { id: 'sec-11', label: '11. Security' },
    { id: 'sec-12', label: '12. Children' },
    { id: 'sec-13', label: '13. Automated Analysis and AI' },
    { id: 'sec-14', label: '14. Third-Party Websites and Services' },
    { id: 'sec-15', label: '15. Changes to This Privacy Policy' },
    { id: 'sec-16', label: '16. Contact Us' },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://aisearchadvisers.com/#organization",
        "name": "AI Search Advisers",
        "url": "https://aisearchadvisers.com/",
        "description": "AI Search Advisers helps businesses assess and improve website readiness for AI-driven discovery through website audits, AI visibility insights, crawlability analysis, structured data evaluation, and prioritized recommendations.",
        "email": "info@aisearchadvisers.com"
      },
      {
        "@type": "WebSite",
        "@id": "https://aisearchadvisers.com/#website",
        "url": "https://aisearchadvisers.com/",
        "name": "AI Search Advisers",
        "publisher": { "@id": "https://aisearchadvisers.com/#organization" },
        "inLanguage": "en-US"
      },
      {
        "@type": "WebPage",
        "@id": "https://aisearchadvisers.com/privacy-policy#webpage",
        "url": "https://aisearchadvisers.com/privacy-policy",
        "name": "Privacy Policy | AI Search Advisers",
        "description": "Read the AI Search Advisers Privacy Policy to understand how information is collected, used, stored, and protected when using our website and services.",
        "isPartOf": { "@id": "https://aisearchadvisers.com/#website" },
        "about": { "@id": "https://aisearchadvisers.com/#organization" },
        "publisher": { "@id": "https://aisearchadvisers.com/#organization" },
        "inLanguage": "en-US"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] font-sans text-[#021426] flex flex-col selection:bg-[#FEAB3D]/30 selection:text-[#021426]">
      <PageSEO 
        title="Privacy Policy | AI Search Advisers"
        description="Read the AI Search Advisers Privacy Policy to understand how information is collected, used, stored, and protected when using our website and services."
        canonical="https://aisearchadvisers.com/privacy-policy"
        schema={schema}
      />
      <GlobalHeader />

      <LegalHero 
        eyebrow="LEGAL"
        title="Privacy Policy"
        supportingText="AI Search Advisers"
        effectiveDate="September 21, 2026"
        lastUpdated="September 21, 2026"
      />

      {/* CONTENT & SIDEBAR */}
      <section className="px-6 py-16 relative z-20">
        <div className="max-w-[1100px] mx-auto flex flex-col lg:flex-row gap-12 lg:gap-20 items-start">
          
          {/* SIDEBAR NAVIGATION (Desktop) */}
          <div className="hidden lg:block w-64 shrink-0 sticky top-32">
            <h4 className="font-['Fraunces',serif] text-lg font-bold text-[#021426] mb-4">On this page</h4>
            <div className="flex flex-col gap-3 border-l-2 border-[#e5e9ee] pl-4">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left text-sm text-[#4C545B] hover:text-[#0C5B94] hover:translate-x-1 transition-all font-medium"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* MAIN CONTENT */}
          <div className="flex-1 max-w-[800px] legal-content w-full">
            
            <p>AI Search Advisers respects your privacy and is committed to handling personal information responsibly and transparently.</p>
            <p>This Privacy Policy explains what information we collect, how we use it, when we share it, how long we retain it, and the choices and rights available to you when you use our website, software, audits, analytics, reports, and related services.</p>

            <h2 id="sec-1">1. Who We Are</h2>
            <p>AI Search Advisers provides software and services that help businesses understand and improve how they appear in AI-powered search and discovery systems.</p>
            <p>For privacy-related questions or requests, you may contact us through the contact or support channels provided on the AI Search Advisers website.</p>

            <h2 id="sec-2">2. Scope of This Policy</h2>
            <p>This Privacy Policy applies in two main situations.</p>
            
            <h3>When You Use AI Search Advisers</h3>
            <p>This includes information relating to you as a website visitor, prospect, account holder, or customer.</p>

            <h3>When We Process Analytics for Your Website</h3>
            <p>When you use our analytics features, we may process information about visitors to your website on your behalf.</p>
            <p>In that situation, you remain responsible for determining the lawful basis for collecting and processing information from your visitors, providing appropriate privacy notices, and handling your visitors' privacy rights.</p>
            <p>We treat these situations separately where appropriate because the legal responsibilities involved may differ.</p>

            <h2 id="sec-3">3. Information We Collect About You</h2>
            <p>We collect information about you when you visit our website, create an account, purchase a subscription, or communicate with us.</p>
            <p>For detailed information on how we use cookies and similar storage technologies on our website, please review our <a href="/cookie-policy">Cookie Policy</a>.</p>
            
            <h3>3.1 Information You Provide</h3>
            <p>Depending on how you use the Service, you may provide us with:</p>
            <ul>
              <li><strong>Account information</strong>, such as your name, email address, business name, and account credentials.</li>
              <li><strong>Billing information</strong>, including billing name, billing address, and applicable tax information. Payment card details are submitted directly to our payment processor and are not stored directly by AI Search Advisers.</li>
              <li><strong>Audit information</strong>, including website domains that you ask us to analyze.</li>
              <li><strong>Communications</strong>, including messages sent through forms, email, support channels, or phone conversations.</li>
              <li><strong>Optional business information</strong>, including information provided when requesting eligibility for veteran-owned business pricing.</li>
            </ul>

            <h3>3.2 Information Collected Automatically</h3>
            <p>When you use our website or Service, we may automatically collect limited technical information necessary to operate, protect, and improve the Service.</p>
            <p>This may include:</p>
            <ul>
              <li>IP address</li>
              <li>Browser type</li>
              <li>Device type</li>
              <li>Pages viewed</li>
              <li>Referring source</li>
              <li>Date and time of activity</li>
            </ul>

            <h3>3.3 Information From Third Parties</h3>
            <p>We may receive information from:</p>
            <ul>
              <li><strong>Payment processors</strong>, including transaction status and subscription information.</li>
              <li><strong>Publicly available sources</strong> when performing an audit or analysis requested by you.</li>
            </ul>
            <p>Publicly available information may include website content, technical configuration files, business listings, public search results, and AI-generated search or discovery results.</p>
            <p>We do not intentionally attempt to access private or password-protected information when conducting these audits.</p>

            <h3>3.4 Sensitive Information</h3>
            <p>We do not seek to collect sensitive personal information such as health information, government identification numbers, or special-category personal data unless it is specifically required for a legitimate service purpose and permitted by law.</p>

            <h2 id="sec-4">4. How We Use Information</h2>
            <p>We may use personal information to:</p>
            <ul>
              <li>Provide, operate, and maintain AI Search Advisers.</li>
              <li>Perform the audits, reports, and analyses you request.</li>
              <li>Create and manage accounts.</li>
              <li>Process subscription and service payments.</li>
              <li>Send reports, alerts, service notifications, and account communications.</li>
              <li>Respond to customer support requests.</li>
              <li>Improve the reliability and accuracy of our services.</li>
              <li>Detect and prevent fraud, abuse, unauthorized access, and security incidents.</li>
              <li>Comply with applicable legal obligations.</li>
              <li>Enforce our Terms of Service.</li>
              <li>Send marketing communications where permitted by law.</li>
            </ul>
            <p>You may unsubscribe from marketing communications at any time.</p>
            <p>We do <strong>not sell personal information</strong>.</p>
            <p>We do not share personal information for cross-context behavioral advertising.</p>
            <p>We do not use your personal information or your customers' information to train third-party AI models.</p>

            <h2 id="sec-5">5. Legal Bases for Processing</h2>
            <p>Where the General Data Protection Regulation, UK GDPR, or similar privacy laws apply, we process personal information under one or more appropriate legal bases.</p>
            <p>These may include:</p>
            <ul>
              <li><strong>Performance of a contract</strong> when processing is necessary to provide a service you requested.</li>
              <li><strong>Legal obligations</strong> where information must be processed or retained to comply with applicable law.</li>
              <li><strong>Legitimate interests</strong> for purposes such as security, fraud prevention, service reliability, and service improvement.</li>
              <li><strong>Consent</strong> where consent is required, including certain marketing activities.</li>
            </ul>
            <p>Where processing is based on legitimate interests, you may have the right to object to that processing.</p>

            <h2 id="sec-6">6. Analytics We Provide About Your Website Visitors</h2>
            <p>When you use AI Search Advisers analytics features, we process certain information on your behalf.</p>
            
            <h3>Cookieless by Design</h3>
            <p>Our website analytics are designed not to rely on cookies placed on your visitors' devices and not to use browser fingerprinting to track individuals across unrelated websites.</p>
            
            <h3>Information Processed</h3>
            <p>Analytics information may include:</p>
            <ul>
              <li>Page views</li>
              <li>Referring source</li>
              <li>Approximate geographic location</li>
              <li>Device type</li>
              <li>Browser type</li>
              <li>On-site actions</li>
            </ul>
            
            <h3>IP Addresses</h3>
            <p>IP addresses may be processed temporarily for purposes such as deriving approximate geographic location, security, fraud detection, and filtering automated traffic.</p>
            <p>We do not retain identifiable IP address information longer than reasonably necessary for these operational purposes.</p>
            
            <h3>Your Responsibilities</h3>
            <p>If you install or use AI Search Advisers analytics on your website, you are responsible for:</p>
            <ul>
              <li>Maintaining an appropriate privacy notice for your visitors.</li>
              <li>Establishing an appropriate lawful basis for processing.</li>
              <li>Providing notices and obtaining consent where legally required.</li>
              <li>Responding to privacy rights requests from your website visitors.</li>
            </ul>
            <p>Where applicable, additional data-processing terms can be made available on request.</p>

            <h2 id="sec-7">7. Sharing Information</h2>
            <p>We share personal information only where reasonably necessary to operate the Service or satisfy legal obligations.</p>
            <p>Information may be shared with:</p>
            
            <h3>Service Providers</h3>
            <p>We may use third-party service providers for functions such as:</p>
            <ul>
              <li>Website hosting</li>
              <li>Infrastructure</li>
              <li>Payment processing</li>
              <li>Email delivery</li>
              <li>Customer support</li>
              <li>Error monitoring</li>
              <li>Security</li>
            </ul>
            <p>These providers process information on our behalf and are expected to handle it appropriately.</p>
            <p>Information about relevant subprocessors may be made available on request.</p>
            
            <h3>AI and Search Providers</h3>
            <p>When performing an audit, we may submit queries relating to the business or domain being analyzed to third-party AI systems, search engines, or other information providers.</p>
            <p>These queries are intended to concern the business being audited rather than the individual who requested the audit.</p>
            
            <h3>Professional Advisers</h3>
            <p>We may disclose information to lawyers, accountants, auditors, or similar professional advisers where reasonably necessary and subject to appropriate confidentiality obligations.</p>
            
            <h3>Legal and Safety Requirements</h3>
            <p>Information may be disclosed where required by law or where reasonably necessary to protect our rights, users, systems, or the public.</p>
            
            <h3>Business Transfers</h3>
            <p>Information may be transferred in connection with a merger, acquisition, restructuring, financing transaction, or sale of business assets.</p>
            <p>If such a transaction materially changes how personal information is handled, affected users will be notified where required.</p>

            <h2 id="sec-8">8. International Data Transfers</h2>
            <p>AI Search Advisers is based in the United States, and some of our service providers may operate in other countries.</p>
            <p>Where applicable privacy laws require safeguards for international transfers of personal information, we use legally recognized mechanisms and protections.</p>
            <p>These may include the European Commission's Standard Contractual Clauses and applicable UK data-transfer safeguards.</p>

            <h2 id="sec-9">9. How Long We Keep Information</h2>
            <p>We retain personal information only for as long as reasonably necessary to:</p>
            <ul>
              <li>Provide the Service.</li>
              <li>Maintain your account.</li>
              <li>Maintain reports and service history.</li>
              <li>Meet contractual obligations.</li>
              <li>Resolve disputes.</li>
              <li>Maintain security.</li>
              <li>Comply with accounting, tax, regulatory, and legal obligations.</li>
            </ul>
            <p>Different categories of information may be retained for different periods.</p>
            <p>For example:</p>
            <ul>
              <li>Account information may be retained while your account remains active and for a reasonable period afterward.</li>
              <li>Audit reports may be retained while needed to provide report history and account functionality.</li>
              <li>Billing and tax information may be retained where required by applicable law.</li>
              <li>Support communications may be retained for customer-service, security, and record-keeping purposes.</li>
              <li>Website analytics data may be retained according to the applicable service configuration.</li>
              <li>Temporary technical information such as IP addresses may be deleted or anonymized after it is no longer required for operational or security purposes.</li>
              <li>Marketing contact details may be retained until you unsubscribe, after which limited information may remain on a suppression list so that your preference can be respected.</li>
            </ul>
            <p>We delete or anonymize personal information when it is no longer reasonably required.</p>

            <h2 id="sec-10">10. Your Privacy Rights</h2>
            <p>Depending on where you live, you may have rights relating to your personal information.</p>
            <p>These may include the right to:</p>
            <ul>
              <li>Request access to personal information we hold about you.</li>
              <li>Request correction of inaccurate or incomplete information.</li>
              <li>Request deletion of your information.</li>
              <li>Request a portable copy of certain information.</li>
              <li>Object to certain processing.</li>
              <li>Request restriction of certain processing.</li>
              <li>Withdraw consent where processing is based on consent.</li>
              <li>Opt out of marketing communications.</li>
              <li>Exercise privacy rights without unlawful discrimination.</li>
            </ul>
            <p>To exercise a privacy right, contact AI Search Advisers through the contact or support channels provided on our website.</p>
            <p>We may need to verify your identity before completing certain requests.</p>
            <p>We will respond within the period required by applicable law.</p>
            <p>Residents of jurisdictions including the European Economic Area, United Kingdom, and California may have additional rights under applicable privacy laws.</p>
            <p>We do not sell or share personal information as those terms are defined under applicable California privacy law.</p>

            <h2 id="sec-11">11. Security</h2>
            <p>We use administrative, technical, and organizational safeguards designed to protect personal information.</p>
            <p>These measures may include:</p>
            <ul>
              <li>Encryption in transit</li>
              <li>Encryption at rest where appropriate</li>
              <li>Access controls</li>
              <li>Restricted access based on business need</li>
              <li>Security monitoring</li>
              <li>Measures designed to detect unauthorized activity</li>
            </ul>
            <p>No online service or information system can be guaranteed to be completely secure.</p>
            <p>If a security incident involving personal information requires notification under applicable law, we will provide the required notifications.</p>

            <h2 id="sec-12">12. Children</h2>
            <p>AI Search Advisers is intended for business use and is not directed toward children under the age of 16.</p>
            <p>We do not knowingly collect personal information from children under 16.</p>
            <p>If you believe that a child has provided personal information to us, please contact us so that we can review and, where appropriate, delete the information.</p>

            <h2 id="sec-13">13. Automated Analysis and AI</h2>
            <p>AI Search Advisers reports may be produced using automated analysis and artificial intelligence systems applied to publicly available information.</p>
            <p>These reports are informational.</p>
            <p>They are not intended to make legal, employment, financial, credit, or similarly significant decisions about individuals.</p>
            <p>AI Search Advisers does not use these reports for automated decision-making of the type described in Article 22 of the GDPR.</p>
            <p>Our findings describe observed signals at a particular point in time.</p>
            <p>They do not guarantee how ChatGPT, Perplexity, Gemini, Claude, Google, another search engine, or another AI system will behave in the future.</p>

            <h2 id="sec-14">14. Third-Party Websites and Services</h2>
            <p>AI Search Advisers may analyze, reference, or link to third-party websites and services.</p>
            <p>We do not control the privacy practices of those third parties.</p>
            <p>Their handling of information is governed by their own privacy policies and terms.</p>
            <p>We encourage you to review those policies when using third-party services.</p>

            <h2 id="sec-15">15. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time.</p>
            <p>When changes are made, the updated version will be published on this page with a revised "Last updated" date.</p>
            <p>Where required by law, we may also provide additional notice of material changes to registered users.</p>
            <p>Your continued use of the Service after an updated policy becomes effective is subject to the revised policy where permitted by applicable law.</p>

            <h2 id="sec-16">16. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, your personal information, or your privacy rights, please contact AI Search Advisers through the official contact or support channels available on our website.</p>
            <p className="mt-8 font-semibold"><a href="mailto:info@aisearchadvisers.com">info@aisearchadvisers.com</a></p>
          </div>
        </div>
      </section>

      <GlobalFooter />
    </div>
  );
}
